import { TENANT_ROLES, type TenantRole } from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Plus } from "lucide-react";
import { useState } from "react";

import { useSessionQuery } from "../lib/authQueries";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";

const ROLE_DESCRIPTIONS: Record<TenantRole, string> = {
  viewer: "Read-only across the tenant",
  editor: "Manage boxes, storefront, top-up packs and shopper credits",
  admin: "Everything, including payments, API keys and this team",
};

/** The accept link for an invitation, built the way team.invite builds it. */
function inviteLink(invitationId: string): string {
  return `${window.location.origin}/accept-invite?id=${invitationId}`;
}

function CopyLinkButton({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check /> : <Copy />}
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

export function TeamPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();

  const team = useQuery(trpc.team.list.queryOptions());
  const invitations = useQuery(trpc.team.invitations.queryOptions());

  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("editor");
  // The last successful invite, kept up so the link can be copied post-send,
  // with whether the email actually went out (SES sandbox, unverified recipient).
  const [sent, setSent] = useState<{ link: string; emailed: boolean } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.team.list.queryOptions().queryKey });
    void queryClient.invalidateQueries({
      queryKey: trpc.team.invitations.queryOptions().queryKey,
    });
  };

  const invite = useMutation(
    trpc.team.invite.mutationOptions({
      onSuccess: ({ link, emailed }) => {
        invalidate();
        setSent({ link, emailed });
        setEmail("");
      },
    }),
  );
  const cancelInvite = useMutation(
    trpc.team.cancelInvite.mutationOptions({ onSuccess: invalidate }),
  );
  const updateRole = useMutation(trpc.team.updateRole.mutationOptions({ onSuccess: invalidate }));
  const remove = useMutation(trpc.team.remove.mutationOptions({ onSuccess: invalidate }));
  const closeInvite = () => {
    setShowInvite(false);
    setSent(null);
    invite.reset();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">
            Who can sign in to this tenant's dashboard, and what they can do. Invites go out by
            email and as a link you can share directly; they expire after 7 days.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => {
            invite.reset();
            setSent(null);
            setShowInvite(true);
          }}
        >
          <Plus />
          Invite member
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            Admins manage everything including this page; editors manage content; viewers read.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {team.data?.members.map((member) => {
                const isSelf = member.userId === session?.user.id;
                // Legacy roles (owner, member) render as-is; the select only
                // offers the invitable three.
                const selectable = (TENANT_ROLES as readonly string[]).includes(member.role);
                return (
                  <TableRow key={member.memberId}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {member.name || member.email}
                          {isSelf && (
                            <span className="ml-2 text-xs text-muted-foreground">you</span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">{member.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {selectable ? (
                        <Select
                          value={member.role}
                          onValueChange={(value) =>
                            updateRole.mutate({
                              memberId: member.memberId,
                              role: value as TenantRole,
                            })
                          }
                        >
                          <SelectTrigger className="w-32" disabled={updateRole.isPending}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TENANT_ROLES.map((r) => (
                              <SelectItem key={r} value={r}>
                                <span className="capitalize">{r}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="secondary" className="capitalize">
                          {member.role}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(member.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={remove.isPending}
                            />
                          }
                        >
                          Remove
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Remove {member.name || member.email}?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              They lose access to this tenant immediately. Their account stays;
                              re-invite them any time.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove.mutate({ memberId: member.memberId })}
                            >
                              Remove member
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending invitations</CardTitle>
          <CardDescription>
            Waiting to be accepted. Re-inviting the same email replaces its pending invite.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No pending invitations.
                  </TableCell>
                </TableRow>
              )}
              {invitations.data?.map((invitation) => {
                const expired = new Date(invitation.expiresAt).getTime() < Date.now();
                return (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {invitation.role ?? "viewer"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(invitation.expiresAt)}
                      {expired && <span className="ml-2 text-xs text-destructive">expired</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        {!expired && <CopyLinkButton link={inviteLink(invitation.id)} />}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={cancelInvite.isPending}
                          onClick={() => cancelInvite.mutate({ invitationId: invitation.id })}
                        >
                          Cancel
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showInvite} onOpenChange={(open) => !open && closeInvite()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              They get an email with an accept link (and you get the same link to share directly).
              New to Loot OS? The link lets them create their account.
            </DialogDescription>
          </DialogHeader>
          {sent ? (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-sm">
                {sent.emailed
                  ? "Invitation emailed. Share this link directly if it doesn't arrive:"
                  : "The invitation is saved, but the email couldn't be sent. Share this link directly:"}
              </p>
              {/* min-w-0 on the row: it's a grid item of DialogContent, whose
                  default min-width:auto would let the unbreakable URL widen the
                  track past the panel — the code's own truncate can't save it. */}
              <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                <code className="min-w-0 flex-1 truncate font-mono text-xs">{sent.link}</code>
                <div className="shrink-0">
                  <CopyLinkButton link={sent.link} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSent(null)}>
                  Invite another
                </Button>
                <Button type="button" onClick={closeInvite}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                invite.mutate({ email: email.trim(), role });
              }}
            >
              <div className="grid gap-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@example.com"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Role</Label>
                <Select value={role} onValueChange={(value) => setRole(value as TenantRole)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TENANT_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        <span className="capitalize">{r}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {ROLE_DESCRIPTIONS[r]}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeInvite}>
                  Cancel
                </Button>
                <Button type="submit" disabled={invite.isPending || !email.trim()}>
                  {invite.isPending ? "Inviting…" : "Send invite"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
